// Utility functions for the application

/**
 * Get a color based on a string (e.g., for resource categories)
 * @param str - The string to derive a color from
 * @returns A tailwind color class
 */
export function getColorFromString(str: string): string {
  const colors = [
    'bg-blue-500',
    'bg-green-500',
    'bg-red-500',
    'bg-yellow-500',
    'bg-purple-500',
    'bg-pink-500',
    'bg-indigo-500',
    'bg-teal-500',
  ];
  
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  hash = Math.abs(hash);
  return colors[hash % colors.length];
}

/**
 * Get a text color based on a string
 * @param str - The string to derive a color from
 * @returns A tailwind text color class
 */
export function getTextColorFromString(str: string): string {
  const colors = [
    'text-blue-500',
    'text-green-500',
    'text-red-500',
    'text-yellow-500',
    'text-purple-500',
    'text-pink-500',
    'text-indigo-500',
    'text-teal-500',
  ];
  
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  hash = Math.abs(hash);
  return colors[hash % colors.length];
}

/**
 * Truncate a string to a maximum length
 * @param str - The string to truncate
 * @param maxLength - The maximum length
 * @returns The truncated string
 */
export function truncate(str: string, maxLength: number): string {
  return str.length > maxLength 
    ? str.substring(0, maxLength) + '...'
    : str;
}

/**
 * Get the domain from a URL
 * @param url - The URL to extract the domain from
 * @returns The domain name
 */
export function getDomainFromUrl(url: string): string {
  try {
    const urlObj = new URL(url);
    return urlObj.hostname;
  } catch (e) {
    // If URL is invalid, return the original URL
    return url;
  }
}

/**
 * Get favicon URL for a website - optimized method using favicon cache
 * @param url - The website URL
 * @param localIcon - Optional local icon path or URL
 * @returns The favicon URL
 */
export function getFaviconUrl(url: string, localIcon?: string): string {
  // 如果有配置本地图标，则优先使用本地图标
  if (localIcon && localIcon.trim()) {
    if (localIcon.startsWith('http')) {
      // 如果是完整 URL，直接返回
      return localIcon;
    } else if (localIcon.startsWith('/')) {
      // 如果是以 / 开头的路径，视为本地路径
      return localIcon;
    }
    // 否则，假设它是 /icons/ 目录下的图标名称
    return `/icons/${localIcon}`;
  }
  
  try {
    // 获取域名
    const domain = getDomainFromUrl(url);
    
    // 服务器端本地缓存列表
    const knownFavicons: Record<string, string> = {
      // AI 网站
      'claude.ai': 'https://t2.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://claude.ai&size=32',
      'chatgpt.com': 'https://t2.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://chatgpt.com&size=32',
      'chat.deepseek.com': 'https://t2.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://chat.deepseek.com&size=32',
      'bard.google.com': 'https://t2.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://bard.google.com&size=32',
      'www.perplexity.ai': 'https://t2.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://perplexity.ai&size=32',
      
      // Web 数据工具
      'analytics.google.com': 'https://t1.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://analytics.google.com&size=32',
      'search.google.com': 'https://t2.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://google.com&size=32',
      'www.similarweb.com': 'https://t2.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://similarweb.com&size=32',
      
      // 开发工具
      'github.com': 'https://t2.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://github.com&size=32',
      'stackoverflow.com': 'https://t2.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://stackoverflow.com&size=32',
      'code.visualstudio.com': 'https://t2.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://code.visualstudio.com&size=32',
      'www.jetbrains.com': 'https://t2.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://jetbrains.com&size=32',
      'reactjs.org': 'https://t2.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://reactjs.org&size=32',
      'nodejs.org': 'https://t2.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://nodejs.org&size=32',
      
      // 其他网站
      'www.toolify.ai': 'https://t2.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://toolify.ai&size=32',
      'bolt.new': 'https://t2.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://bolt.new&size=32',
      'instantdomainsearch.com': 'https://t2.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://instantdomainsearch.com&size=32',
      'www.spaceship.com': 'https://t2.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://spaceship.com&size=32',
      'vercel.com': 'https://t2.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://vercel.com&size=32',
      'www.cloudflare.com': 'https://t2.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://cloudflare.com&size=32',
      'ahrefs.com': 'https://t2.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://ahrefs.com&size=32',
      'www.semrush.com': 'https://t2.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://semrush.com&size=32'
    };
    
    // Return from local cache if available (fastest)
    if (knownFavicons[domain]) {
      return knownFavicons[domain];
    }
    
    // For non-cached domains, use more reliable Google favicon service
    return `https://t2.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=${domain}&size=32`;
  } catch (error) {
    // 如果出现错误，返回一个安全的默认图标URL
    return 'https://t3.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=example.com&size=32';
  }
} 